import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Eraser, PenLine } from 'lucide-react';

// Panel de firma. `onChange` avisa al terminar cada trazo; `onClear` cuando la
// firma queda vacía (botón Limpiar o un cambio de ancho que obliga a
// redimensionar el lienzo).
//
// clearOnResize va en false: en un celular, abrir o cerrar el teclado dispara
// `resize` y la librería borraría la firma sin avisar. Aquí solo se
// redimensiona (y se avisa) cuando cambia el ANCHO del lienzo, que es lo único
// que descuadra el trazo (girar el teléfono).
const SignaturePad = forwardRef(({ onChange, onClear }, ref) => {
  const padRef = useRef(null);
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useImperativeHandle(ref, () => ({
    isEmpty: () => padRef.current?.isEmpty() ?? true,
    getBlob: () =>
      new Promise((resolve) => {
        if (!padRef.current || padRef.current.isEmpty()) return resolve(null);
        padRef.current.getCanvas().toBlob((b) => resolve(b), 'image/png');
      }),
    clear: () => padRef.current?.clear(),
  }));

  useEffect(() => {
    let ancho = padRef.current?.getCanvas?.()?.offsetWidth;
    const alCambiar = () => {
      const pad = padRef.current;
      const canvas = pad?.getCanvas?.();
      if (!canvas || canvas.offsetWidth === ancho) return;
      ancho = canvas.offsetWidth;
      if (typeof pad._resizeCanvas === 'function') pad._resizeCanvas();
      else pad.clear();
      onClearRef.current?.();
    };
    window.addEventListener('resize', alCambiar);
    return () => window.removeEventListener('resize', alCambiar);
  }, []);

  const limpiar = () => {
    padRef.current?.clear();
    onClearRef.current?.();
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-card">
      <div className="relative">
        {/* baseline hint */}
        <div className="pointer-events-none absolute inset-x-6 bottom-8 flex items-center gap-2 text-muted-foreground/50">
          <PenLine size={14} />
          <div className="h-px flex-1 border-t border-dashed border-border" />
        </div>
        <SignatureCanvas
          ref={padRef}
          penColor="#1C1917"
          clearOnResize={false}
          canvasProps={{ className: 'w-full h-48 rounded-lg touch-none', 'aria-label': 'Área de firma' }}
          onEnd={() => onChange?.()}
        />
      </div>
      <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-1">
        <span className="text-sm font-medium text-muted-foreground">Firme con el dedo</span>
        <button
          type="button"
          onClick={limpiar}
          className="inline-flex min-h-[48px] items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <Eraser size={16} aria-hidden="true" />
          Limpiar
        </button>
      </div>
    </div>
  );
});
export default SignaturePad;
